import { IGenericModal, Modal } from 'components/Modal/Modal';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { Flex } from 'components';
import {
  Button,
  FieldLabel,
  SwitchButton,
  TextField,
  UserSelect
} from 'components/_form';
import { postAuditForm } from 'api/audits';
import { IPostAuditFormRequest } from 'types/audits';
import { useEffect, useState } from 'react';
import {
  NestedSelect,
  IOption as OptionForSelect
} from 'components/_form/NestedSelect/NestedSelect';
import { collapseSelectedAreaPermissionIds } from 'components/_form/NestedSelect/parseNestedSelectSelectedLabels';
import { findOptionByValueInTree } from 'components/_form/NestedSelect/utilities';
import { IUserSelectOption } from 'utilities/userSelectOption';
import { IOption } from 'components/_form/Select/Select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPersonCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { clearCriteriaFromIds } from 'utilities/auditForm/clearCriteriaFromIds';
import { clearGroupsFromIds } from 'utilities/auditForm/clearGroupsFromIds';
import { showRequestError } from 'utilities/showRequestError';
import { useNavigate } from 'react-router-dom';
import {
  parsePinnedToSelectValue,
  pinnedToSelectValue,
  PinnedToType
} from 'utilities/pinnedTo';
import {
  AuditFormApprovalSchemeModal,
  AuditFormApprovalSchemeValues
} from '../AuditFormApprovalSchemeModal/AuditFormApprovalSchemeModal';

interface Props extends IGenericModal {
  defaultValues: IPostAuditFormRequest['audit_form'];
  methodologyTreeForSelect: OptionForSelect[];
  areas: OptionForSelect[];
  usersList: IUserSelectOption[];
}

export const CopyForm = ({
  isOpen,
  onCancelClick,
  defaultValues,
  methodologyTreeForSelect,
  areas,
  usersList
}: Props) => {
  const { t } = useTranslation();
  const [isApprovalSchemeModalOpen, setIsApprovalSchemeModalOpen] =
    useState(false);

  const navigate = useNavigate();

  const methods = useForm<IPostAuditFormRequest['audit_form']>({
    defaultValues: {}
  });

  const { handleSubmit, reset, control, register, watch, setValue } = methods;
  const requiresApproval = !!watch('requires_approval');
  const approvalSchemeDefaultValues: AuditFormApprovalSchemeValues = {
    execution_progress_tracking_id:
      watch('execution_progress_tracking_id') ?? undefined,
    in_this_order: !!watch('in_this_order'),
    approvers_attributes: watch('approvers_attributes') ?? []
  };

  const onSubmit = handleSubmit(async (data) => {
    const {
      id,
      criteria_attributes,
      groups_attributes,
      approvers_attributes,
      ...restData
    } = data;

    const payload = {
      audit_form: {
        ...restData,
        criteria_attributes: clearCriteriaFromIds(criteria_attributes),
        groups_attributes: clearGroupsFromIds(groups_attributes),
        approvers_attributes: (approvers_attributes || []).map(
          ({ id: _id, execution_progress_tracking_approval_steps_attributes, ...approver }) => ({
            ...approver,
            execution_progress_tracking_approval_steps_attributes: (
              execution_progress_tracking_approval_steps_attributes || []
            ).map(
              ({
                id: _stepId,
                ...step
              }) => step
            )
          })
        )
      }
    };

    const auditFormRes = await postAuditForm(payload);

    if ('error' in auditFormRes) {
      showRequestError(auditFormRes.error, t);
      return;
    }

    navigate(
      `/app/administration/audits/edit-audit-form/${auditFormRes.data.id}`
    );
  });

  const handleCancelClick = () => {
    reset();
    onCancelClick();
  };

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  return (
    <>
      <Modal
        gridTemplateColumns="40%"
        isOpen={isOpen}
        mainButton={{
          action: onSubmit,
          variant: 'eucalyptus',
          label: t('common.copy')
        }}
        secondButton={{
          action: handleCancelClick,
          label: t('common.cancel')
        }}
        isSecondButtonVisible
        headerTitle={t('administrationAuditsView.copyForm')}
        onCancelClick={handleCancelClick}
      >
      <Flex flexDirection="column" gap="20px">
        <Controller
          control={control}
          name="pinned_to_id"
          render={() => {
            const pinnedToType = watch('pinned_to_type') as
              | PinnedToType
              | undefined;
            const pinnedToId = watch('pinned_to_id');
            const selectValue =
              pinnedToType && pinnedToId
                ? pinnedToSelectValue(pinnedToType, pinnedToId)
                : undefined;
            const selected = selectValue
              ? findOptionByValueInTree(methodologyTreeForSelect, selectValue)
              : undefined;

            return (
              <NestedSelect
                width="100%"
                label={t('administrationAuditsView.pinTo')}
                selectedOptions={selected ? [selected] : []}
                setSelectedOptions={(newOption) => {
                  const parsed = parsePinnedToSelectValue(newOption[0]?.value);
                  setValue('pinned_to_type', parsed?.pinned_to_type);
                  setValue('pinned_to_id', parsed?.pinned_to_id);
                }}
                options={methodologyTreeForSelect}
                isOneOptionSelect
                singleSelectAnyNode
              />
            );
          }}
        />

        <TextField {...register('name')} />

        <Controller
          control={control}
          name="area_ids"
          render={({ field: { onChange, value } }) => (
            <NestedSelect
              label={t('administrationAuditsView.usedInAreas') as string}
              selectedOptions={(value ?? [])
                .map((vid) => findOptionByValueInTree(areas, vid))
                .filter((o): o is OptionForSelect => !!o)}
              setSelectedOptions={(newOptions) => {
                onChange(
                  collapseSelectedAreaPermissionIds(areas, newOptions).map(
                    (id) => String(id)
                  )
                );
              }}
              options={areas}
            />
          )}
        />

        <Flex gap="20px">
          <Controller
            control={control}
            name="author_id"
            render={({ field: { onChange, value } }) => (
              <UserSelect
                disabled
                label={t('administrationAuditsView.createdBy')}
                selectedOptions={usersList.find(
                  (userOption) => userOption.value === value
                )}
                onChange={(option: IOption) => {
                  onChange(option.value);
                }}
                options={usersList}
              />
            )}
          />

          <Flex flexDirection="column">
            <FieldLabel>{t('administrationAuditsView.active')}</FieldLabel>
            <Flex mt={2}>
              <Controller
                control={control}
                name="state"
                render={({ field: { onChange, value } }) => (
                  <SwitchButton
                    value={value === 'active'}
                    onChange={(checked) => {
                      onChange(checked ? 'active' : 'inactive');
                    }}
                  />
                )}
              />
            </Flex>
          </Flex>

          <Flex flexDirection="column">
            <FieldLabel whiteSpace="nowrap">
              {t('administrationAuditsView.requireConfirmation')}
            </FieldLabel>
            <Flex mt={2} gap="10px">
              <Controller
                control={control}
                name="requires_approval"
                render={({ field: { onChange, value } }) => (
                  <SwitchButton value={!!value} onChange={onChange} />
                )}
              />
              <Button
                disabled={!requiresApproval}
                onClick={() => setIsApprovalSchemeModalOpen(true)}
              >
                <Flex gap="5px">
                  <FontAwesomeIcon size="xl" icon={faPersonCircleCheck} />
                  {t('administrationAuditsView.panel')}
                </Flex>
              </Button>
            </Flex>
          </Flex>
        </Flex>

        <Controller
          control={control}
          name="person_authorized_to_edit_ids"
          render={({ field: { onChange, value } }) => (
            <UserSelect
              label={t('administrationAuditsView.authorizedToEdit')}
              selectedOptions={
                usersList.filter((userOption) =>
                  value?.includes(userOption.value)
                ) || []
              }
              isMulti
              onChange={(val) => {
                onChange(
                  Array.isArray(val) ? val.map(({ value }) => value) : []
                );
              }}
              options={usersList}
              withoutMargin
            />
          )}
        />
      </Flex>
    </Modal>

      <AuditFormApprovalSchemeModal
        isOpen={isApprovalSchemeModalOpen}
        onCancelClick={() => setIsApprovalSchemeModalOpen(false)}
        defaultValues={approvalSchemeDefaultValues}
        onSave={(values) => {
          setValue(
            'execution_progress_tracking_id',
            values.execution_progress_tracking_id ?? null
          );
          setValue('in_this_order', !!values.in_this_order);
          setValue('approvers_attributes', values.approvers_attributes ?? []);
        }}
      />
    </>
  );
};
